/**
 * Hook that fetches and manages agent data for chat sessions.
 *
 * Handles two concerns:
 * 1. Fetching agent details by ID (public vs authenticated endpoint)
 * 2. Restoring agent selection from persisted selectedAgent IDs
 *
 * @module hooks/use-agent-data
 */
import { useEffect, useState } from "react";
import type { Agent } from "@/types/agent-types";
import { AgentRecord } from "@/types/chat/agent";
import { agentsService } from "@/services/agents-service";

/**
 * Fetches agent details and restores agent selection from IDs.
 *
 * @param agentId - Optional agent ID to fetch details for
 * @param selectedAgentIds - Optional array of agent IDs to restore from a saved chat
 */
export function useAgentData(
  agentId: string | null,
  selectedAgentIds: string[] = [],
) {
  const [agentData, setAgentData] = useState<AgentRecord | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);

  /** Fetch agent details when agentId changes */
  useEffect(() => {
    if (!agentId) {
      setAgentData(null);
      setAgents([]);
      return;
    }

    const fetchAgentData = async () => {
      try {
        const response = await agentsService.getAgent(agentId);

        if (response.success && response.data) {
          const agentRecord: AgentRecord = {
            id: response.data.id,
            name: response.data.name,
            description: response.data.description,
            workspaceId: response.data.workspaceId,
            avatar: response.data.avatar,
            profile: response.data.profile,
            systemPrompt: response.data.systemPrompt,
            welcomeMessage: response.data.welcomeMessage,
            tools: response.data.tools,
            dataSources: null,
            knowledgeBase: response.data.knowledgeBase,
            agentMode: response.data.agentMode,
            published: response.data.published,
            publishedId: response.data.publishedId,
            integrations: response.data.integrations,
            agentSettings: response.data
              .agentSettings as AgentRecord["agentSettings"],
            modelConfig: response.data
              .modelConfig as AgentRecord["modelConfig"],
            /**
             * What chat actually RUNS, which is the published snapshot — the
             * live `modelConfig` above is the draft the builder edits. The two
             * differ whenever a model change has not been published.
             */
            servedModelConfig: response.data
              .servedModelConfig as AgentRecord["modelConfig"],
            createdBy: response.data.createdBy,
            createdAt: new Date(response.data.createdAt),
            updatedAt: response.data.updatedAt
              ? new Date(response.data.updatedAt)
              : null,
          };
          setAgentData(agentRecord);

          setAgents([
            {
              id: response.data.id,
              name: response.data.name,
              description: response.data.description || "",
              avatar: response.data.avatar,
              workspaceId: response.data.workspaceId,
            } as Agent,
          ]);
        }
      } catch {
        setAgentData(null);
      }
    };

    fetchAgentData();
  }, [agentId]);

  /**
   * Restore agent selection from persisted IDs when loading existing chats.
   * Only runs when there's no agentId (multi-agent mode) and agents haven't been set yet.
   */
  useEffect(() => {
    if (!selectedAgentIds || selectedAgentIds.length === 0) return;
    if (agents.length > 0) return;

    const restoreAgentsFromIds = async () => {
      try {
        const response = await agentsService.getPublishedAgents({ limit: 100 });

        if (response.success && response.data) {
          const matchedAgents = response.data.filter((agent: Agent) =>
            selectedAgentIds.includes(agent.id),
          );

          if (matchedAgents.length > 0) {
            setAgents(matchedAgents);
          }
        }
      } catch {
        // Silently fail — agents will just show as unselected
      }
    };

    restoreAgentsFromIds();
  }, [selectedAgentIds, agents.length]);

  return { agentData, agents, setAgents };
}
